import React, { useEffect, useState } from "react";
import { QuestionTypes } from "../../../../utils";
import { Questions, Option } from "Models/checklist";
import { Button, Checkbox, Input } from "Components/Form";
import useStyles, {
  Mandatory,
  PreviewQuestionContent,
  PreviewQuestionDescription,
  PreviewQuestionTitle,
  PreviewQuestionWrapper,
} from "./styles";
import RadioGroup from "Components/Form/RadioGroup";
import { useTranslation } from "react-i18next";
import { Color } from "Components/Form/Button";

interface PreviewQuestionProps {
  question: Questions;
  answers: Record<string, string>;
  onAnswerChange: (questionKey: string, value: string) => void;
}

const getQuestionKey = (question: Questions): string => {
  return question.id?.toString() || question.temporaryId || "";
};

const checkLogicCondition = (
  logicCondition: string,
  logicValue: string,
  answerValue: string
): boolean => {
  switch (logicCondition) {
    case "contains": {
      return answerValue.includes(logicValue);
    }

    case "is": {
      if (logicValue === "blank") {
        return answerValue === "";
      }
      return answerValue === logicValue;
    }

    case "is_not": {
      if (logicValue === "blank") {
        return answerValue !== "";
      }
      return answerValue !== logicValue;
    }

    case "selected": {
      if (!answerValue) return false;
      const selectedNames = answerValue.split(",").map((name) => name.trim());
      return selectedNames.some((name) => name.includes(logicValue));
    }

    case "not_selected": {
      if (!answerValue) return true;
      const selectedNames = answerValue.split(",").map((name) => name.trim());
      return !selectedNames.some((name) => name.includes(logicValue));
    }

    case "is_greater_than": {
      const numericAnswer = parseFloat(answerValue);
      const numericLogicValue = parseFloat(logicValue);
      if (isNaN(numericAnswer) || isNaN(numericLogicValue)) {
        return false;
      }
      return numericAnswer > numericLogicValue;
    }

    case "is_lesser_than": {
      const numericAnswer = parseFloat(answerValue);
      const numericLogicValue = parseFloat(logicValue);
      if (isNaN(numericAnswer) || isNaN(numericLogicValue)) {
        return false;
      }
      return numericAnswer < numericLogicValue;
    }

    default:
      console.warn(`Unknown logic condition: ${logicCondition}`);
      return false;
  }
};

export const PreviewQuestion = ({ question, answers, onAnswerChange }: PreviewQuestionProps) => {
  const [answer, setAnswer] = useState<string>("");

  const { t: translate } = useTranslation();

  const classes = useStyles();

  const questionKey = getQuestionKey(question);

  useEffect(() => {
    const currentAnswer = answers[questionKey] || "";
    setAnswer(currentAnswer);
  }, [answers, question.id]);

  const handleAnswerChange = (value: string) => {
    setAnswer(value);
    onAnswerChange(questionKey, value);
  };

  const getVisibleNestedQuestions = () => {
    if (!question.questionLogics || question.questionLogics.length === 0) {
      return [];
    }

    const visibleQuestions: Questions[] = [];

    for (const logic of question.questionLogics) {
      const isMet = checkLogicCondition(logic.logicCondition!, logic.logicValue || "", answer);

      if (isMet && logic.questions) {
        visibleQuestions.push(...logic.questions);
      }
    }

    return visibleQuestions;
  };

  const renderInput = (question: Questions) => {
    if (question.type === QuestionTypes.DATE) {
      return (
        <PreviewQuestionContent>
          <PreviewQuestionTitle>
            {question.title}
            {question.mandatory && <Mandatory>{"*"}</Mandatory>}
          </PreviewQuestionTitle>

          <PreviewQuestionDescription>{question.description || " "}</PreviewQuestionDescription>

          <Input
            name="date"
            type="date"
            value={answer}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              handleAnswerChange(event.target.value);
            }}
          />
        </PreviewQuestionContent>
      );
    }

    if (question.type === QuestionTypes.NUMBER) {
      return (
        <PreviewQuestionContent>
          <PreviewQuestionTitle>
            {question.title}
            {question.mandatory && <Mandatory>{"*"}</Mandatory>}
          </PreviewQuestionTitle>

          <PreviewQuestionDescription>{question.description || " "}</PreviewQuestionDescription>

          <Input
            name="number"
            type="number"
            value={answer}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              handleAnswerChange(event.target.value);
            }}
          />
        </PreviewQuestionContent>
      );
    }

    return (
      <PreviewQuestionContent>
        <PreviewQuestionTitle>
          {question.title}
          {question.mandatory && <Mandatory>{"*"}</Mandatory>}
        </PreviewQuestionTitle>

        <PreviewQuestionDescription>{question.description || " "}</PreviewQuestionDescription>

        <Input
          name="title"
          value={answer}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            handleAnswerChange(event.target.value);
          }}
        />
      </PreviewQuestionContent>
    );
  };

  const renderSingleChoice = (question: Questions) => {
    return (
      <PreviewQuestionContent>
        <PreviewQuestionTitle>
          {question.title}
          {question.mandatory && <Mandatory>{"*"}</Mandatory>}
        </PreviewQuestionTitle>

        <PreviewQuestionDescription>{question.description || " "}</PreviewQuestionDescription>

        <RadioGroup
          row={false}
          value={answer}
          className={classes.radioGroup}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            handleAnswerChange(event.target.value)
          }
          options={
            question.options
              ?.filter((option) => option.valid)
              ?.map((option) => ({
                label: option.name,
                value: option.name,
              })) || []
          }
        />
      </PreviewQuestionContent>
    );
  };

  interface CheckboxesFieldProps {
    question: Questions;
    value?: string; // Comma-separated name of selected options
    onChange?: (value: string) => void;
  }

  const CheckboxesField = ({ question, value, onChange }: CheckboxesFieldProps) => {
    const [options, setOptions] = useState<Option[]>([]);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

    useEffect(() => {
      setOptions(question.options?.filter((option) => option.valid) || []);

      // Parse the comma-separated value into a Set of IDs
      if (value) {
        const ids = value.split(",").filter((id) => id.trim());
        setSelectedIds(new Set(ids));
      } else {
        setSelectedIds(new Set());
      }
    }, [value]);

    const handleCheckboxChange = (optionId: string, isChecked: boolean) => {
      const newSelectedIds = new Set(selectedIds);

      if (isChecked) {
        newSelectedIds.add(optionId);
      } else {
        newSelectedIds.delete(optionId);
      }

      setSelectedIds(newSelectedIds);

      // Convert back to comma-separated string
      const newValue = Array.from(newSelectedIds).join(",");
      onChange?.(newValue);
    };

    return (
      <div>
        {options.map((option) => (
          <Checkbox
            key={option.name}
            label={option.name}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              handleCheckboxChange(option.name, event?.target.checked)
            }
            checked={selectedIds.has(option.name)}
          />
        ))}
      </div>
    );
  };

  const renderMultipleChoice = (question: Questions) => {
    return (
      <PreviewQuestionContent>
        <PreviewQuestionTitle>
          {question.title}
          {question.mandatory && <Mandatory>{"*"}</Mandatory>}
        </PreviewQuestionTitle>

        <PreviewQuestionDescription>{question.description || " "}</PreviewQuestionDescription>

        <CheckboxesField
          question={question}
          value={answer}
          onChange={(value: string) => handleAnswerChange(value)}
        />
      </PreviewQuestionContent>
    );
  };

  const renderPhoto = (question: Questions) => {
    return (
      <PreviewQuestionContent>
        <PreviewQuestionTitle>
          {question.title}
          {question.mandatory && <Mandatory>{"*"}</Mandatory>}
        </PreviewQuestionTitle>

        <PreviewQuestionDescription>{question.description || " "}</PreviewQuestionDescription>

        <Button customColor={Color.BRAND_OUTLINED}>{translate("addAPhoto")}</Button>
      </PreviewQuestionContent>
    );
  };

  const renderSignature = (question: Questions) => {
    return (
      <PreviewQuestionContent>
        <PreviewQuestionTitle>
          {question.title}
          {question.mandatory && <Mandatory>{"*"}</Mandatory>}
        </PreviewQuestionTitle>

        <PreviewQuestionDescription>{question.description || " "}</PreviewQuestionDescription>

        <Button customColor={Color.BRAND_OUTLINED}>{translate("addSignature")}</Button>
      </PreviewQuestionContent>
    );
  };

  const renderQuestionFieldByType = (question: Questions) => {
    const { type } = question;
    if (!type) {
      return null;
    }

    switch (type) {
      case QuestionTypes.DATE:
      case QuestionTypes.NUMBER:
      case QuestionTypes.TEXT:
        return renderInput(question);
      case QuestionTypes.SINGLE:
        return renderSingleChoice(question);
      case QuestionTypes.MULTIPLE:
        return renderMultipleChoice(question);
      case QuestionTypes.PHOTO:
        return renderPhoto(question);
      case QuestionTypes.SIGNATURE:
        return renderSignature(question);
      default:
        return null;
    }
  };

  const visibleNestedQuestions = getVisibleNestedQuestions();

  return (
    <>
      <PreviewQuestionWrapper>{renderQuestionFieldByType(question)}</PreviewQuestionWrapper>
      {visibleNestedQuestions.map((nestedQuestion) => (
        <PreviewQuestion
          key={nestedQuestion.id || nestedQuestion.temporaryId}
          question={nestedQuestion}
          answers={answers}
          onAnswerChange={onAnswerChange}
        />
      ))}
    </>
  );
};
